import { History } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { missingRequired } from "./answerValidation";
import { toast } from "sonner";
import { QuestionnaireForm } from "./QuestionnaireForm";
import { REVIEW_ID } from "./SectionNav";
import type { ResponseOut, ResponsePatch, Template } from "./types";
import { UploadContext, type UploadContextValue } from "./uploadContext";
import { useMe } from "@/api/hooks/auth";
import { useFiles } from "@/api/hooks/files";
import { usePatchResponse, useQuestionnaire, useReopenResponse, useSubmitResponse } from "@/api/hooks/questionnaire";
import { errorMessage, isApiError } from "@/api/errors";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { FormError } from "@/components/Field";
import { PageLoading } from "@/components/Spinner";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { uploadFile } from "@/features/files/upload";
import { useProjectParams } from "@/features/projects/projectContext";
import { formatDate } from "@/lib/dates";
import { Markdown } from "@/lib/markdown";

export const blankResponse: ResponseOut = {
  id: "new",
  template_id: "",
  status: "draft",
  answers: {},
  section_progress: {},
  review_flags: [],
  new_question_ids: [],
  submitted_at: null,
  submitted_by: null,
  answered_by: {},
  updated_at: null,
};

function hasFileAnswers(template: Template | undefined, response: ResponseOut | null | undefined): boolean {
  if (!template || !response) return false;
  return template.sections.some((s) =>
    s.questions.some((q) => q.type === "file_upload" && Array.isArray(response.answers[q.id]) && (response.answers[q.id] as string[]).length > 0),
  );
}

export function QuestionnairePage() {
  const { t } = useTranslation();
  const { org, project } = useProjectParams();
  const me = useMe();
  const q = useQuestionnaire(org, project);
  const [search] = useSearchParams();
  // A save was refused because a colleague submitted first.
  const [lostEdit, setLostEdit] = useState(false);
  const version = q.data?.template?.version ?? 0;
  const patch = usePatchResponse(org, project, version);
  const submit = useSubmitResponse(org, project, version);
  const reopen = useReopenResponse(org, project, version);
  const files = useFiles(org, project, { enabled: hasFileAnswers(q.data?.template?.body, q.data?.response) });

  const uploadCtx = useMemo<UploadContextValue>(
    () => ({
      upload: async (file, onProgress) => {
        const out = await uploadFile(org, project, file, { title: file.name }, onProgress);
        return { id: out.id, title: out.title };
      },
      fileName: (id) => files.data?.find((f) => f.id === id)?.title,
    }),
    [org, project, files.data],
  );

  if (q.isPending) return <PageLoading />;
  if (q.isError) return <ErrorNotice error={q.error} />;
  const { template } = q.data;
  if (!template) return <EmptyState title={t("questionnaire.emptyTitle")} description={t("questionnaire.emptyBody")} />;

  const isStaff = Boolean(me.data?.is_staff);
  const response = q.data.response ?? blankResponse;
  const submitted = response.status === "submitted";
  const readOnly = isStaff || submitted;

  const onPatch = async (body: ResponsePatch) => {
    try {
      await patch.mutateAsync(body);
    } catch (e) {
      if (!isApiError(e) || e.status !== 409) throw e;
      // A colleague submitted. Not retried: reload so the form shows what the server holds.
      setLostEdit(true);
      await q.refetch();
    }
  };
  const onSubmit = () => {
    submit.mutate(undefined, {
      onError: (e) => {
        if (e.status !== 409) return;
        // A colleague submitted first: show what the server holds, with the same notice as a refused save.
        setLostEdit(true);
        submit.reset();
        void q.refetch();
      },
    });
  };
  const onMarkDone = (sectionId: string) => {
    patch.mutate(
      { section_progress: { [sectionId]: "done" } },
      { onError: (e) => toast.error(errorMessage(e, t("errors.generic"))) },
    );
  };

  // Deep links: ?section=<id> opens that section; ?resume=1 opens the first one with a required gap.
  const firstGap = search.get("resume") ? missingRequired(template.body, response.answers)[0]?.sectionId : undefined;
  // With nothing missing, "resume" lands on the review step where the answers can be submitted.
  const resumeTarget = search.get("resume") ? (firstGap ?? (readOnly ? undefined : REVIEW_ID)) : undefined;
  const initialSectionId = search.get("section") ?? resumeTarget;

  const submitErrors = isApiError(submit.error) ? Object.values(submit.error.errors).flat() : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2>{template.title}</h2>
          <p className="text-sm text-fg-muted">
            {t("questionnaire.versionLabel", { version: template.version })} ·{" "}
            {t("questionnaire.publishedOn", { date: formatDate(template.published_at) })}
          </p>
        </div>
        {template.version > 1 ? (
          <Link to="history" className="inline-flex min-h-11 items-center gap-2 text-sm font-medium">
            <History aria-hidden="true" className="h-4 w-4" />
            {t("questionnaire.history")}
          </Link>
        ) : null}
      </div>

      {template.body.intro ? <Markdown source={template.body.intro} className="max-w-prose" /> : null}

      {template.notes && template.version > 1 ? (
        <div className="rounded-lg border border-secondary-muted bg-surface p-5">
          <h3 className="mb-1 text-lg">{t("questionnaire.whatChanged", { version: template.version })}</h3>
          <Markdown source={template.notes} />
        </div>
      ) : null}

      {isStaff ? <p className="rounded-md bg-surface-sunken px-4 py-3 text-sm">{t("questionnaire.staffReadOnly")}</p> : null}

      {submitted ? (
        <div role="status" className="flex flex-col gap-3 rounded-lg border border-secondary bg-secondary px-6 py-5 text-fg-inverse sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1">
            <p className="font-semibold">{t("questionnaire.submittedBanner", { date: formatDate(response.submitted_at) })}</p>
            {response.submitted_by ? (
              <p>
                {t("questionnaire.submittedBy", { name: response.submitted_by.name, date: formatDate(response.submitted_at) })}
              </p>
            ) : null}
          </div>
          {!isStaff ? (
            <Button
              variant="secondary"
              className="border-surface"
              disabled={reopen.isPending}
              onClick={() => {
                setLostEdit(false);
                reopen.mutate(undefined, { onError: (e) => toast.error(errorMessage(e, t("errors.generic"))) });
              }}
            >
              {reopen.isPending ? t("questionnaire.reopening") : t("questionnaire.reopen")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {submitted && lostEdit ? (
        <Banner tone="warning">{t("questionnaire.lostEdit", { name: response.submitted_by?.name ?? t("questionnaire.aColleague") })}</Banner>
      ) : null}

      {submit.isError ? (
        <FormError message={submitErrors.length > 0 ? submitErrors.join(" ") : errorMessage(submit.error, t("errors.generic"))} />
      ) : null}

      <UploadContext.Provider value={uploadCtx}>
        <QuestionnaireForm
          // Not keyed on response.id: the first autosave creates the response, and remounting
          // then would drop focus, the "Saved" status and anything typed meanwhile.
          key={`${template.version}-${response.status}`}
          template={template.body}
          response={response}
          onPatch={onPatch}
          onMarkDone={onMarkDone}
          onSubmit={onSubmit}
          readOnly={readOnly}
          submitting={submit.isPending}
          initialSectionId={initialSectionId}
          complete={submitted}
          version={template.version}
          addedQuestionIds={template.added_question_ids}
          changedQuestionIds={template.changed_question_ids}
          staffPreview={isStaff}
          currentUserId={me.data?.id}
        />
      </UploadContext.Provider>
    </div>
  );
}
